import { useMemo, useRef, useEffect, useState, useCallback } from "react";
import {
  useActiveCategories,
  useItemsGroupedByCategory,
  useMenuStations,
} from "../../../../hooks/useMenu";
import { ItemRow } from "./ItemRow";
import { ItemCard } from "./ItemCard";
import { SearchKeyboard } from "./SearchKeyboard";
import {
  buildGroupColorLookup,
  itemColor,
  stationColorOf,
  type ColorBy,
} from "./catalogColors";
import type {
  CatalogSort,
  KeyboardLayout,
  OrderingSearchScope,
} from "../../../../api/configuration";
import { byPluMatch, catalogComparator } from "./catalogSort";
import { Empty } from "antd";
import { SearchOutlined, CloseCircleFilled } from "@ant-design/icons";
import { getTranslation } from "../../../../utils/translations";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import { createSearchMatcher, matchesPluCode } from "../../../../utils/search";

interface OrderItem {
  itemId: number;
  quantity: number;
  modifiers: number[];
  addons: Record<number, Record<number, number>>; // addonId -> { itemId -> количество }
}

interface ItemCatalogProps {
  categoryId: number | null;
  selectedItemId: number | null;
  onItemSelect: (itemId: number | null) => void;
  isModalOpen?: boolean;
  orderItems?: OrderItem[];
  onQuantityChange?: (itemId: number, quantity: number) => void;
  // Моливът в степера на реда/картата — отваря EditModifiersModal (Ordering)
  onItemEdit?: (itemId: number) => void;
  onBack?: () => void;
  // Продукти, за които + отваря ModifyItemModal (задължителни/препоръчителни
  // добавки или задължителен модификатор).
  itemsRequiringAddons?: Set<number>;
  // Увеличава се всеки път, когато ModifyItemModal е затворен с "Добави" — тогава стартираме clear.
  addonModalAddSignal?: number;
  // Изглед на каталога (лична настройка catalogView, Плочки/Таблица във
  // футъра): "list" = редове (по подразбиране), "grid" = карти в решетка.
  // Гридът важи само на десктоп — на телефон винаги са stacked редове.
  view?: "list" | "grid";
  // Подредба на продуктите (лична настройка catalogSort): по име или по PLU
  // код — вътре във всяка група в списъка и в целия грид.
  sort?: CatalogSort;
  // Знаков процент по сметката (клиент с отстъпка/надбавка) — редовете/картите
  // показват ~~стара~~ ±X% нова цена; 0 = без корекция
  billDiscountPercent?: number;
  // Филтър по станция (когато над каталога стоят станциите — ресторантска
  // настройка catalogFilter = "stations"); null = всички
  stationId?: number | null;
  // По какво се оцветяват редовете/картите (настройка на ресторанта colorBy)
  colorBy?: ColorBy;
  // Екранната клавиатура (F8 / бутонът във футъра) — десктоп; пише в
  // searchTerm като физическата. Подредбата е личната настройка.
  keyboardOpen?: boolean;
  keyboardLayout?: KeyboardLayout;
  onKeyboardClose?: () => void;
  // Настройка на ресторанта orderingSearchField: на десктоп полето за
  // търсене се вижда (true) или е скрито (false, по подразбиране) — тогава
  // написаното личи само по оцветените съвпадения в списъка. На
  // телефон/таблет полето е винаги видимо.
  searchFieldVisible?: boolean;
  // Настройка на ресторанта orderingSearchScope — къде търси търсачката при
  // избрана група: "menu" (по подразбиране) — първата буква вика
  // onExitCategory и търси в цялото меню; "group" — само в групата и след
  // добавяне се остава в нея.
  searchScope?: OrderingSearchScope;
  // Сваля избраната група (родителят нулира категорията) — при searchScope "menu".
  onExitCategory?: () => void;
}

/**
 * Продуктовият каталог на „Маркиране" — общ за ДВАТА изгледа (настройката
 * от Конфигурация → Маркиране): "list" = секции по категория с продуктови
 * редове (ItemRow); "grid" = един плосък грид с карти (ItemCard), само на
 * десктоп. Редът на продуктите е личната настройка `sort` (по име / по PLU,
 * `catalogSort.ts`). Търсачката, филтрите и клавиатурната машина (по DOM
 * позиции) са общи за двата изгледа.
 */
export const ItemCatalog = ({
  categoryId,
  selectedItemId,
  onItemSelect,
  isModalOpen = false,
  orderItems = [],
  onQuantityChange,
  onItemEdit,
  onBack,
  itemsRequiringAddons,
  addonModalAddSignal,
  view = "list",
  sort = "alphabetical",
  billDiscountPercent = 0,
  stationId = null,
  colorBy = "group",
  keyboardOpen = false,
  keyboardLayout = "phonetic",
  onKeyboardClose,
  searchFieldVisible = false,
  searchScope = "menu",
  onExitCategory,
}: ItemCatalogProps) => {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  // Keyboard навигацията и focus sync-ът важат само за десктоп
  const { isDesktop, isNarrow } = useViewport();
  const showSearchField = !isDesktop || searchFieldVisible;
  // Грид изгледът е само за десктоп — телефонът остава на stacked редове
  const isGrid = isDesktop && view === "grid";
  const [focusedItemIndex, setFocusedItemIndex] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const isKeyboardNavRef = useRef(false);
  const onItemSelectRef = useRef(onItemSelect);
  const onQuantityChangeRef = useRef(onQuantityChange);
  const orderItemsRef = useRef(orderItems);
  const onBackRef = useRef(onBack);
  // Реф към полето за търсене — на телефон връщаме фокуса в него след
  // зачистване, за да остане клавиатурата отворена за следващото търсене; на
  // десктоп (с видимо поле) по него keydown handler-ът познава, че фокусът е
  // в самото поле.
  const searchInputRef = useRef<HTMLInputElement>(null);
  // ID на текущо фокусирания продукт (следи focusedItemIndex) — нужно на
  // авто-clear-а, за да върне фокуса върху същия продукт в новия списък.
  const focusedItemIdRef = useRef<number | null>(null);
  // Продукт, върху който да остане фокусът след авто-clear на търсачката
  // (след − / + или „Добави"), за да може с + да се добавят още бройки.
  const restoreFocusItemIdRef = useRef<number | null>(null);
  // Индексът, зададен от възстановяването — докато state-ът го достигне,
  // sync ефектът не бива да „избере" продукта на стария индекс в новия списък.
  const pendingRestoreIndexRef = useRef<number | null>(null);
  // Зачиства търсачката; САМО на телефон връща фокуса в полето. На десктоп
  // фокусът остава където е бил — в списъка върху последно маркирания
  // продукт или в самото поле, ако е видимо и човек е бил в него.
  // focusItemId — продуктът, на който да останем; без него е фокусираният.
  // Кликът с мишката го подава изрично: в същия handler фокусът му е само
  // поискан (setFocusedItemIndex), а focusedItemIdRef още сочи стария —
  // иначе след зачистване оставаме на първия резултат, не на цъкнатия.
  const clearSearchKeepFocus = useCallback(
    (focusItemId?: number) => {
      // Само ако наистина има какво да се чисти — при празно поле няма смяна
      // на списъка, която да консумира запомнения продукт (би „изскочил" при
      // следващата смяна).
      if (isDesktop && searchTerm)
        restoreFocusItemIdRef.current =
          focusItemId ?? focusedItemIdRef.current;
      setSearchTerm("");
      if (!isDesktop) searchInputRef.current?.focus();
    },
    [isDesktop, searchTerm],
  );
  const clearSearchKeepFocusRef = useRef(clearSearchKeepFocus);
  useEffect(() => {
    clearSearchKeepFocusRef.current = clearSearchKeepFocus;
  }, [clearSearchKeepFocus]);
  const itemsRequiringAddonsRef = useRef(itemsRequiringAddons);
  useEffect(() => {
    itemsRequiringAddonsRef.current = itemsRequiringAddons;
  }, [itemsRequiringAddons]);

  // Keep refs updated
  useEffect(() => {
    onItemSelectRef.current = onItemSelect;
    onQuantityChangeRef.current = onQuantityChange;
    orderItemsRef.current = orderItems;
    onBackRef.current = onBack;
  }, [onItemSelect, onQuantityChange, orderItems, onBack]);

  // Щом потребителят започне ново търсене (празен → въведен знак) при
  // searchScope "menu" — излизаме от избраната група и търсим в цялото меню.
  // При "group" търсенето остава в групата и след добавяне се стои в нея.
  // Еднакво в „Маркиране" и в редактора на обедно меню.
  const prevSearchTermRef = useRef("");
  const exitCategoryOnSearchRef = useRef<(() => void) | undefined>(undefined);
  useEffect(() => {
    exitCategoryOnSearchRef.current =
      searchScope === "menu" ? onExitCategory : undefined;
  }, [searchScope, onExitCategory]);
  useEffect(() => {
    if (!prevSearchTermRef.current && searchTerm) {
      exitCategoryOnSearchRef.current?.();
    }
    prevSearchTermRef.current = searchTerm;
  }, [searchTerm]);

  // След като ModifyItemModal се затвори с "Добави за {сума}", зачистваме търсачката.
  const didMountAddonSignalRef = useRef(false);
  useEffect(() => {
    if (addonModalAddSignal === undefined) return;
    if (!didMountAddonSignalRef.current) {
      didMountAddonSignalRef.current = true;
      return;
    }
    if (!searchTerm) return;
    clearSearchKeepFocusRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addonModalAddSignal]);

  // Вземаме артикулите групирани по категория.
  const groupedItems = useItemsGroupedByCategory(categoryId);

  // Цветове: станциите (от базата) и извлечените цветове на групите — за
  // фона на редовете/картите (colorBy) и колоната „Станция" в списъка.
  const stations = useMenuStations();
  const categories = useActiveCategories();
  const stationsById = useMemo(
    () => new Map(stations.map((station) => [station.id, station])),
    [stations],
  );
  const groupColor = useMemo(
    () => buildGroupColorLookup(categories),
    [categories],
  );
  // Продукт в няколко станции: при активен филтър по станция се показва
  // (и оцветява) с КЛИКНАТАТА станция, иначе с първата си
  const stationIdOf = useCallback(
    (item: (typeof groupedItems)[number]["items"][number]) =>
      stationId !== null && item.stationIds.includes(stationId)
        ? stationId
        : item.stationIds[0],
    [stationId],
  );
  const tintOf = useCallback(
    (item: (typeof groupedItems)[number]["items"][number]) =>
      colorBy === "station"
        ? stationColorOf(stationsById.get(stationIdOf(item)))
        : itemColor(item, colorBy, stationsById, groupColor),
    [colorBy, stationsById, groupColor, stationIdOf],
  );
  const stationOf = useCallback(
    (item: (typeof groupedItems)[number]["items"][number]) => {
      const station = stationsById.get(stationIdOf(item));
      if (!station) return null;
      return {
        name: getTranslation(station.translations, i18n.language, station.slug),
        color: stationColorOf(station),
      };
    },
    [stationsById, i18n.language, stationIdOf],
  );

  // Името на продукта на текущия език — за търсенето и подредбата
  const nameOf = useCallback(
    (item: (typeof groupedItems)[number]["items"][number]) =>
      item.itemLangs.find((l) => l.languageCode === i18n.language)?.name ||
      item.itemLangs[0]?.name ||
      item.slug,
    [i18n.language],
  );
  // Компараторът по личната настройка: по име или по PLU код (catalogSort.ts)
  const compareItems = useMemo(
    () => catalogComparator(sort, nameOf, i18n.language),
    [sort, nameOf, i18n.language],
  );

  // Филтрираме само enabled продуктите (и по станция, ако е избрана).
  // Продуктите ВЪТРЕ в група се подреждат по compareItems — групите остават
  // по sortIndex.
  const enabledGroupedItems = useMemo(() => {
    return groupedItems
      .map((group) => ({
        ...group,
        items: group.items
          .filter(
            (item) =>
              item.enabled &&
              (stationId === null || item.stationIds.includes(stationId)),
          )
          .sort(compareItems),
      }))
      .filter((group) => group.items.length > 0);
  }, [groupedItems, compareItems, stationId]);

  // Филтрира по заявка. Многодумно търсене: заявката се цепи по интервали и
  // ВСЯКА дума трябва да се среща в името (независимо къде) — „са раз" намира
  // продукт, съдържащ и „са", и „раз". Търсенето е и на „шльокавица"
  // (`utils/search.ts`) — „минерална вода" излиза на „мин", „м в", „min" и
  // „m v". Заявка само от цифри намира и по PLU код (префикс — виж
  // `utils/search.ts`); тези съвпадения излизат ВИНАГИ най-отгоре (и в
  // списъка, и в грида) в отделна секция без категориен хедър, а
  // съвпадналите и по име не се повтарят в групата си.
  const filterByTerm = useCallback(
    (term: string) => {
      const pluIds = new Set<number>();
      for (const group of enabledGroupedItems) {
        for (const item of group.items) {
          if (matchesPluCode(item.pluCode, term)) pluIds.add(item.id);
        }
      }
      const matcher = createSearchMatcher(term);
      if (matcher.isEmpty) return { groups: enabledGroupedItems, pluIds };

      const nameGroups = enabledGroupedItems
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => {
            if (pluIds.has(item.id)) return false;
            // Търсим на текущия език, ако няма - на следващия наличен език
            const currentLang = item.itemLangs.find(
              (lang) => lang.languageCode === i18n.language,
            );
            const nameToSearch =
              currentLang?.name || item.itemLangs[0]?.name || "";
            return matcher.matches(nameToSearch);
          }),
        }))
        .filter((group) => group.items.length > 0);

      if (pluIds.size === 0) return { groups: nameGroups, pluIds };

      const pluMatches = enabledGroupedItems
        .flatMap((group) => group.items)
        .filter((item) => pluIds.has(item.id))
        .sort(byPluMatch);
      // Сентинел -1 — да не се сблъска ключът с истинската група „без категория"
      // (categoryId null); category: null → секция без хедър.
      return {
        groups: [
          { categoryId: -1, category: null, items: pluMatches },
          ...nameGroups,
        ],
        pluIds,
      };
    },
    [enabledGroupedItems, i18n.language],
  );

  // Заявка без съвпадения (напр. „кафе" + случаен знак) НЕ изпразва списъка:
  // показва се най-дългият ѝ префикс, който има съвпадения, а
  // hasNoMatches прави рамката на търсачката и буквите след този префикс
  // червени. Подчертаването в редовете е по показаната заявка
  // (effectiveSearchTerm), не по написаната.
  // Двете memo-та са разделени нарочно: effectiveSearchTerm е низ и не се
  // променя при „кафе" → „кафеx", така че списъкът долу запазва референцията
  // си и фокусът/подчертаването не трепват.
  const effectiveSearchTerm = useMemo(() => {
    let term = searchTerm;
    while (term.length > 0 && filterByTerm(term).groups.length === 0) {
      term = term.slice(0, -1);
    }
    return term;
  }, [searchTerm, filterByTerm]);
  const hasNoMatches = effectiveSearchTerm !== searchTerm;

  const { groups: filteredGroupedItems, pluIds: pluMatchedIds } = useMemo(
    () => filterByTerm(effectiveSearchTerm),
    [effectiveSearchTerm, filterByTerm],
  );

  // Scroll to top when category changes
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [categoryId]);

  // Flatten all items for keyboard navigation. В грид изгледа продуктите са
  // ЕДИН плосък списък по compareItems (без категорийни секции) — сортираме и
  // тук, за да съвпада клавиатурният ред с визуалния. PLU съвпаденията обаче
  // остават НАЙ-ОТПРЕД и в грида (както в списъка).
  const allFlatItems = useMemo(() => {
    const flat = filteredGroupedItems.flatMap((group) => group.items);
    if (!isGrid) return flat;
    return [...flat].sort((a, b) => {
      const aPluMatched = pluMatchedIds.has(a.id);
      const bPluMatched = pluMatchedIds.has(b.id);
      if (aPluMatched !== bPluMatched) return aPluMatched ? -1 : 1;
      if (aPluMatched && bPluMatched) return byPluMatch(a, b);
      return compareItems(a, b);
    });
  }, [filteredGroupedItems, isGrid, compareItems, pluMatchedIds]);

  // Актуалният плосък списък за ефекта отдолу — той се задейства САМО при
  // смяна на филтрираните групи (не и при смяна на изглед/език), затова не
  // може да има allFlatItems в зависимостите си.
  const allFlatItemsRef = useRef(allFlatItems);
  useEffect(() => {
    allFlatItemsRef.current = allFlatItems;
  }, [allFlatItems]);

  // При смяна на филтрираните групи фокусът се връща в началото — освен след
  // авто-clear на търсачката, когато остава върху последно маркирания продукт
  // и той се скролва в полезрението (където и да е в менюто).
  useEffect(() => {
    const restoreItemId = restoreFocusItemIdRef.current;
    restoreFocusItemIdRef.current = null;
    const restoreIndex =
      restoreItemId === null
        ? -1
        : allFlatItemsRef.current.findIndex(
            (item) => item.id === restoreItemId,
          );
    if (restoreItemId === null || restoreIndex === -1) {
      setFocusedItemIndex(0);
      return;
    }
    pendingRestoreIndexRef.current = restoreIndex;
    setFocusedItemIndex(restoreIndex);
    // Скролваме директно; гасим и флага на стрелките, за да не превърти
    // scroll ефектът по-долу към продукта на стария индекс в същия commit.
    // "instant" нарочно — без анимация човек е веднага на продукта (при 500
    // продукта плавният скрол се вижда като превъртане през цялото меню);
    // само "auto" не стига, контейнерът е scroll-smooth.
    isKeyboardNavRef.current = false;
    itemRefs.current
      .get(restoreItemId)
      ?.scrollIntoView({ behavior: "instant", block: "center" });
  }, [filteredGroupedItems]);

  // Wrapper за onQuantityChange, който обновява фокуса
  const handleQuantityChange = useCallback(
    (itemId: number, quantity: number) => {
      // Общото текущо количество (по всички редове) — за да разберем дали
      // това е инкремент, който би отворил ModifyItemModal.
      const currentQty = orderItemsRef.current
        .filter((oi) => oi.itemId === itemId)
        .reduce((sum, oi) => sum + oi.quantity, 0);
      const opensAddonsModal =
        quantity > currentQty &&
        (itemsRequiringAddonsRef.current?.has(itemId) ?? false);

      if (onQuantityChange) {
        onQuantityChange(itemId, quantity);
      }

      // Намираме индекса на променения артикул и го фокусираме
      const itemIndex = allFlatItems.findIndex((item) => item.id === itemId);
      if (itemIndex !== -1 && isDesktop) {
        setFocusedItemIndex(itemIndex);
      }

      // Клик с мишката на − / + също зачиства търсачката (както клавиатурните
      // − / +). Ако инкрементът отваря ModifyItemModal, не зачистваме — clear-ът
      // идва след „Добави" (addonModalAddSignal) — дотогава sync ефектът
      // вече е обновил focusedItemIdRef към този продукт. Подаваме itemId, за
      // да останем на цъкнатия продукт, а не на стария фокус.
      if (searchTerm && !opensAddonsModal) {
        clearSearchKeepFocusRef.current(itemId);
      }
    },
    [onQuantityChange, allFlatItems, isDesktop, searchTerm],
  );

  // Sync focused item with selected item - само на десктоп
  useEffect(() => {
    if (!isDesktop) return;
    if (pendingRestoreIndexRef.current !== null) {
      // Същият commit, в който възстановяването зададе нов индекс — тук
      // focusedItemIndex е още старият и би избрал грешен продукт. Флагът се
      // консумира веднага (следващият commit носи новия индекс), за да не
      // може да заседне.
      const pendingIndex = pendingRestoreIndexRef.current;
      pendingRestoreIndexRef.current = null;
      if (pendingIndex !== focusedItemIndex) return;
    }
    const focusedItem = allFlatItems[focusedItemIndex];
    focusedItemIdRef.current = focusedItem?.id ?? null;
    if (focusedItem) {
      onItemSelectRef.current(focusedItem.id);
    }
  }, [focusedItemIndex, allFlatItems, isDesktop]);

  // Keyboard navigation for items
  useEffect(() => {
    if (!isDesktop || isModalOpen) return;

    const handleKeyPress = (event: KeyboardEvent) => {
      // Игнорираме ако вече сме в input поле — освен ако това е САМОТО поле
      // за търсене (настройката orderingSearchField): там ↑/↓, −/+ и Esc се
      // обработват тук (и не се изписват), а буквите, Backspace и ←/→ по
      // курсора остават на самия input. Това е старият дефект „клик в
      // полето изяжда −/+" — полето беше махнато заради него (0.0.242).
      const inSearchField =
        searchInputRef.current !== null &&
        event.target === searchInputRef.current;
      if (
        !inSearchField &&
        event.target instanceof HTMLElement &&
        (event.target.tagName === "INPUT" ||
          event.target.tagName === "TEXTAREA")
      )
        return;

      const key = event.key;

      // В полето ←/→ местят курсора в написаното, а Ctrl/Cmd + „−"/„+" е зуум
      // на браузъра — и двете остават на браузъра.
      if (inSearchField) {
        if (key === "ArrowLeft" || key === "ArrowRight") return;
        if (event.ctrlKey || event.metaKey) return;
      }

      // Arrow navigation using actual DOM positions
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(key)) {
        event.preventDefault();
        isKeyboardNavRef.current = true;

        // Get actual visual positions of all items
        const getItemPositions = () => {
          const positions: { index: number; left: number; top: number }[] = [];
          allFlatItems.forEach((item, index) => {
            const element = itemRefs.current.get(item.id);
            if (element) {
              const rect = element.getBoundingClientRect();
              positions.push({ index, left: rect.left, top: rect.top });
            }
          });
          return positions;
        };

        const positions = getItemPositions();
        const currentPos = positions.find((p) => p.index === focusedItemIndex);

        if (!currentPos) return;

        switch (key) {
          case "ArrowUp": {
            // Find items above current position (smaller top)
            const itemsAbove = positions
              .filter((p) => p.top < currentPos.top - 5) // 5px tolerance
              .sort((a, b) => b.top - a.top); // Sort by top descending

            if (itemsAbove.length > 0) {
              // Group items by row (similar top values)
              const rows: (typeof itemsAbove)[] = [];
              itemsAbove.forEach((item) => {
                const existingRow = rows.find(
                  (row) => Math.abs(row[0].top - item.top) < 5,
                );
                if (existingRow) {
                  existingRow.push(item);
                } else {
                  rows.push([item]);
                }
              });

              // Get first row above (closest row)
              const prevRow = rows[0];
              if (prevRow) {
                // Try to find item in same column
                const sameColumn = prevRow.find(
                  (item) => Math.abs(item.left - currentPos.left) < 20,
                );

                if (sameColumn) {
                  setFocusedItemIndex(sameColumn.index);
                } else {
                  // No item in same column, go to first item of previous row
                  const firstInRow = prevRow.sort((a, b) => a.left - b.left)[0];
                  setFocusedItemIndex(firstInRow.index);
                }
              }
            }
            break;
          }
          case "ArrowDown": {
            // Find items below current position (larger top)
            const itemsBelow = positions
              .filter((p) => p.top > currentPos.top + 5) // 5px tolerance
              .sort((a, b) => a.top - b.top); // Sort by top ascending

            if (itemsBelow.length > 0) {
              // Group items by row (similar top values)
              const rows: (typeof itemsBelow)[] = [];
              itemsBelow.forEach((item) => {
                const existingRow = rows.find(
                  (row) => Math.abs(row[0].top - item.top) < 5,
                );
                if (existingRow) {
                  existingRow.push(item);
                } else {
                  rows.push([item]);
                }
              });

              // Get first row below
              const nextRow = rows[0];
              if (nextRow) {
                // Try to find item in same column
                const sameColumn = nextRow.find(
                  (item) => Math.abs(item.left - currentPos.left) < 20,
                );

                if (sameColumn) {
                  setFocusedItemIndex(sameColumn.index);
                } else {
                  // No item in same column, go to first item of next row
                  const firstInRow = nextRow.sort((a, b) => a.left - b.left)[0];
                  setFocusedItemIndex(firstInRow.index);
                }
              }
            }
            break;
          }
          case "ArrowLeft": {
            // Find items in same row (similar top) to the left
            const itemsInRow = positions
              .filter((p) => Math.abs(p.top - currentPos.top) < 20) // Same row (increased tolerance)
              .filter((p) => p.left < currentPos.left - 5) // To the left
              .sort((a, b) => b.left - a.left); // Sort by left descending

            if (itemsInRow.length > 0) {
              setFocusedItemIndex(itemsInRow[0].index);
            }
            break;
          }
          case "ArrowRight": {
            // Find items in same row (similar top) to the right
            const itemsInRow = positions
              .filter((p) => Math.abs(p.top - currentPos.top) < 20) // Same row (increased tolerance)
              .filter((p) => p.left > currentPos.left + 5) // To the right
              .sort((a, b) => a.left - b.left); // Sort by left ascending

            if (itemsInRow.length > 0) {
              setFocusedItemIndex(itemsInRow[0].index);
            }
            break;
          }
        }
        return;
      }

      // + or = - increment quantity
      if (key === "+" || key === "=") {
        event.preventDefault();
        const focusedItem = allFlatItems[focusedItemIndex];
        let opensAddonsModal = false;
        if (focusedItem && onQuantityChangeRef.current) {
          // Общото количество от всички записи (продуктът може да е на няколко реда)
          const currentQty = orderItemsRef.current
            .filter((item) => item.itemId === focusedItem.id)
            .reduce((sum, item) => sum + item.quantity, 0);
          opensAddonsModal =
            itemsRequiringAddonsRef.current?.has(focusedItem.id) ?? false;
          onQuantityChangeRef.current(focusedItem.id, currentQty + 1);
        }
        // Ако инкрементът отваря ModifyItemModal, не зачистваме — clear-ът идва
        // след като модалът бъде затворен с "Добави" (addonModalAddSignal).
        if (searchTerm && !opensAddonsModal) {
          clearSearchKeepFocusRef.current();
        }
        return;
      }

      // - or _ - decrement quantity
      if (key === "-" || key === "_") {
        event.preventDefault();
        const focusedItem = allFlatItems[focusedItemIndex];
        if (focusedItem && onQuantityChangeRef.current) {
          // Общото количество от всички записи (продуктът може да е на няколко реда)
          const currentQty = orderItemsRef.current
            .filter((item) => item.itemId === focusedItem.id)
            .reduce((sum, item) => sum + item.quantity, 0);
          if (currentQty > 0) {
            onQuantityChangeRef.current(focusedItem.id, currentQty - 1);
          }
        }
        if (searchTerm) {
          clearSearchKeepFocusRef.current();
        }
        return;
      }

      // Escape - изчиства търсенето или връща назад
      if (key === "Escape") {
        event.preventDefault();
        if (searchTerm) {
          return setSearchTerm("");
        }
        if (onBackRef.current) {
          return onBackRef.current();
        }
        return;
      }

      // Оттук надолу е писането. Във видимото поле го върши самият input
      // (onChange), с курсор и всички знаци.
      if (inSearchField) return;

      // Backspace - изтрива последния символ
      if (key === "Backspace") {
        event.preventDefault();
        return setSearchTerm((prev) => prev.slice(0, -1));
      }

      // Добавяме символ ако е буква, цифра или интервал — и ` [ ], които
      // при английска подредба стоят на мястото на ч ш щ от фонетичната
      // (`utils/search.ts` ги чете като тези букви); без Cmd/Ctrl/Alt, за да
      // не глътнем клавишни комбинации (Cmd+[ = назад в браузъра)
      const isPhoneticLayoutKey =
        /^[`[\]]$/.test(key) &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey;
      if (/^[a-zA-Zа-яА-Я0-9 ]$/.test(key) || isPhoneticLayoutKey) {
        event.preventDefault();
        return setSearchTerm((prev) => prev + key);
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [
    isDesktop,
    isModalOpen,
    allFlatItems,
    focusedItemIndex,
    selectedItemId,
    onItemSelect,
    searchTerm,
  ]);

  // Scroll focused item into view — само при клавиатурна навигация
  useEffect(() => {
    if (!isKeyboardNavRef.current) return;
    isKeyboardNavRef.current = false;
    const focusedItem = allFlatItems[focusedItemIndex];
    if (focusedItem) {
      const element = itemRefs.current.get(focusedItem.id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [focusedItemIndex, allFlatItems]);

  return (
    <div className="relative h-full flex flex-col bg-pos-canvas text-pos-ink">
      {/* Търсачка — телефон/таблет винаги; десктоп само при включена
          настройка orderingSearchField. Без нея търсенето на десктоп става
          с клавиатурата през глобалния keydown handler (букви/цифри
          филтрират, Backspace трие, Esc чисти); написаното личи по
          оцветените съвпадения в списъка. С поле handler-ът пак хваща
          стрелките и −/+ от самото поле. */}
      {showSearchField && (
        <div className={`${isNarrow ? "px-3" : "px-5"} pt-3.5 pb-1.5`}>
          <div
            className={`flex items-center gap-2.5 bg-pos-card border rounded-xl px-3.5 transition-colors ${
              isDesktop ? "h-[46px]" : "h-10"
            } ${hasNoMatches ? "border-pos-danger-ink" : "border-pos-line"}`}
          >
            <SearchOutlined className="text-pos-muted text-[18px]" />
            {/* <input> не може да оцвети част от текста → при заявка без
              съвпадения текстът му става прозрачен (курсорът остава), а
              отгоре лежи слой със същия текст, в който буквите след
              последното съвпадение са червени. */}
            <div className="relative flex-1 min-w-0 self-stretch flex items-center">
              <input
                ref={searchInputRef}
                data-catalog-search
                type="text"
                placeholder={t("ordering.searchProduct")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                // На телефон полето се фокусира още при влизане в изгледа,
                // за да е отворена клавиатурата и да се пише веднага; на
                // десктоп фокусът остава в списъка (клавиатурата пише и без
                // фокус в полето).
                autoFocus={!isDesktop}
                aria-invalid={hasNoMatches}
                // 16px на телефон — под 16px iOS Safari зуумва страницата при фокус
                className={`${isDesktop ? "text-sm" : "text-base"} ${
                  hasNoMatches
                    ? "text-transparent caret-pos-ink"
                    : "text-pos-ink"
                } placeholder:text-pos-muted outline-none bg-transparent w-full min-w-0`}
              />
              {hasNoMatches && (
                <div
                  aria-hidden
                  className={`pointer-events-none absolute inset-0 flex items-center overflow-hidden whitespace-pre ${
                    isDesktop ? "text-sm" : "text-base"
                  }`}
                >
                  <span className="text-pos-ink">{effectiveSearchTerm}</span>
                  <span className="text-pos-danger-ink">
                    {searchTerm.slice(effectiveSearchTerm.length)}
                  </span>
                </div>
              )}
            </div>
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="text-pos-muted hover:text-pos-dim text-xs leading-none cursor-pointer"
              >
                <CloseCircleFilled />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Списък със секции по категория */}
      <div
        ref={containerRef}
        className={`flex-1 overflow-y-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
          isNarrow ? "px-3" : "px-5"
        } pb-5 ${showSearchField ? "pt-2" : "pt-3.5"}`}
      >
        {filteredGroupedItems.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <Empty description={t("ordering.noProductsFound")} />
          </div>
        ) : isGrid ? (
          /* ГРИД: един плосък грид с ВСИЧКИ продукти по compareItems — без
             категорийни секции/разделители (категорията остава като ФИЛТЪР
             през chips-овете). auto-fill колони — панелът вдясно яде ~27%,
             затова не са фиксирани breakpoint колони като преди 0.0.116.
             Клавиатурната машина работи по DOM позиции и е обща със списъка;
             allFlatItems е сортиран по същия ред. */
          <div className="grid grid-cols-[repeat(auto-fill,minmax(205px,1fr))] gap-1">
            {allFlatItems.map((item, globalIndex) => (
              <ItemCard
                key={item.id}
                ref={(el) => {
                  if (el) {
                    itemRefs.current.set(item.id, el);
                  } else {
                    itemRefs.current.delete(item.id);
                  }
                }}
                item={item}
                selected={selectedItemId === item.id}
                onSelect={(itemId) => {
                  // Кликът селектира, мести keyboard фокуса и добавя +1 —
                  // същото поведение като реда в списъка (гейтът за
                  // задължителни добавки/модификатори пак отваря „Промени")
                  isKeyboardNavRef.current = false;
                  setFocusedItemIndex(globalIndex);
                  onItemSelect(itemId);
                  const clickQty = orderItems
                    .filter((oi) => oi.itemId === itemId)
                    .reduce((sum, oi) => sum + oi.quantity, 0);
                  handleQuantityChange(itemId, clickQty + 1);
                }}
                searchTerm={effectiveSearchTerm}
                quantity={
                  // Общото количество от всички записи
                  orderItems
                    .filter((oi) => oi.itemId === item.id)
                    .reduce((sum, oi) => sum + oi.quantity, 0)
                }
                onQuantityChange={handleQuantityChange}
                onEdit={onItemEdit}
                billDiscountPercent={billDiscountPercent}
                tint={tintOf(item)}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col">
            {filteredGroupedItems.map((group) => {
              return (
                <div key={group.categoryId ?? "no-group"}>
                  {/* Карта-група с продуктови редове */}
                  <div
                    className={`bg-pos-card border border-pos-line3 ${
                      isNarrow ? "rounded-[13px]" : "rounded-[14px]"
                    } overflow-hidden`}
                  >
                    {group.items.map((item) => {
                      const globalIndex = allFlatItems.findIndex(
                        (i) => i.id === item.id,
                      );

                      return (
                        <ItemRow
                          key={item.id}
                          ref={(el) => {
                            if (el) {
                              itemRefs.current.set(item.id, el);
                            } else {
                              itemRefs.current.delete(item.id);
                            }
                          }}
                          item={item}
                          selected={selectedItemId === item.id}
                          stacked={!isDesktop}
                          onSelect={(itemId) => {
                            if (!isDesktop) {
                              // На телефон кликът винаги селектира (без toggle)
                              onItemSelect(itemId);
                            } else {
                              // На десктоп кликът селектира и мести keyboard фокуса
                              isKeyboardNavRef.current = false;
                              setFocusedItemIndex(globalIndex);
                              onItemSelect(itemId);
                            }
                            // Кликът върху реда добавя +1 — като „+" на степера
                            // (мин. през същия път: гейтът за задължителни
                            // добавки пак отваря „Промени" при нужда)
                            const clickQty = orderItems
                              .filter((oi) => oi.itemId === itemId)
                              .reduce((sum, oi) => sum + oi.quantity, 0);
                            handleQuantityChange(itemId, clickQty + 1);
                          }}
                          searchTerm={effectiveSearchTerm}
                          quantity={
                            // Общото количество от всички записи
                            orderItems
                              .filter((oi) => oi.itemId === item.id)
                              .reduce((sum, oi) => sum + oi.quantity, 0)
                          }
                          onQuantityChange={handleQuantityChange}
                          onEdit={onItemEdit}
                          billDiscountPercent={billDiscountPercent}
                          tint={tintOf(item)}
                          station={isDesktop ? stationOf(item) : null}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Екранна клавиатура — ляга върху долната част на каталога (само
          десктоп; на телефон е системната). Пише в същия searchTerm. */}
      {isDesktop && keyboardOpen && (
        <div className="absolute inset-x-0 bottom-0 z-30 bg-pos-canvas px-5 pt-2 pb-2.5">
          <SearchKeyboard
            value={searchTerm}
            matchedPrefixLength={effectiveSearchTerm.length}
            layout={keyboardLayout}
            onInput={(text) => setSearchTerm((prev) => prev + text)}
            onBackspace={() => setSearchTerm((prev) => prev.slice(0, -1))}
            onClear={() => setSearchTerm("")}
            onHide={() => onKeyboardClose?.()}
          />
        </div>
      )}
    </div>
  );
};
